import React, { useEffect, useRef, useState } from 'react';
import { MonitorUp, Square, Volume2, VolumeX, PictureInPicture2, X } from 'lucide-react';
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Helper for dismissible toasts with X button
const showDismissibleToast = (message, options = {}) => {
    return toast((t) => (
        <div className="flex items-center gap-3">
            <span className="flex-1">{message}</span>
            <button
                onClick={() => toast.dismiss(t.id)}
                className="p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded transition-colors"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    ), { duration: 8000, ...options });
};

const showDismissibleSuccess = (message, options = {}) => {
    return toast.success((t) => (
        <div className="flex items-center gap-3">
            <span className="flex-1">{message}</span>
            <button
                onClick={() => toast.dismiss(t.id)}
                className="p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded transition-colors"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    ), { duration: 8000, ...options });
};

const showDismissibleError = (message, options = {}) => {
    return toast.error((t) => (
        <div className="flex items-center gap-3">
            <span className="flex-1">{message}</span>
            <button
                onClick={() => toast.dismiss(t.id)}
                className="p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded transition-colors"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    ), { duration: 10000, ...options });
};

const ScreenRecorder = ({ onRecordingComplete, clearRecording }) => {

    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [hasAudio, setHasAudio] = useState(false);
    const [isPipActive, setIsPipActive] = useState(false);
    const mediaRecorder = useRef(null);
    const recordedChunks = useRef([]);
    const [recordedBlob, setRecordedBlob] = useState(null);
    const previewVideoRef = useRef(null);
    const displayStreamRef = useRef(null);
    const recordingTimerRef = useRef(null);

    // Draggable bar state
    const [barPosition, setBarPosition] = useState({ x: 0, y: 20 }); // Will be centered on mount
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const barRef = useRef(null);
    const [barInitialized, setBarInitialized] = useState(false);

    useEffect(() => {
        if (clearRecording) {
            clearRecording.current = handleClearRecording;
        }
    }, [clearRecording]);

    // Recording timer
    useEffect(() => {
        if (isRecording) {
            setRecordingTime(0);
            recordingTimerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } else {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
            setRecordingTime(0);
        }
        return () => {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
        };
    }, [isRecording]);

    // Handle PiP events
    useEffect(() => {
        const handlePipEnter = () => setIsPipActive(true);
        const handlePipLeave = () => setIsPipActive(false);

        const video = previewVideoRef.current;
        if (video) {
            video.addEventListener('enterpictureinpicture', handlePipEnter);
            video.addEventListener('leavepictureinpicture', handlePipLeave);
            return () => {
                video.removeEventListener('enterpictureinpicture', handlePipEnter);
                video.removeEventListener('leavepictureinpicture', handlePipLeave);
            };
        }
    }, [isRecording]);

    // Center the bar on initial recording start
    useEffect(() => {
        if (isRecording && barRef.current && !barInitialized) {
            const barWidth = barRef.current.offsetWidth;
            const centerX = (window.innerWidth - barWidth) / 2;
            setBarPosition({ x: centerX, y: 20 });
            setBarInitialized(true);
        }
        if (!isRecording) {
            setBarInitialized(false);
        }
    }, [isRecording, barInitialized]);

    // Drag handlers
    const handleMouseDown = (e) => {
        if (barRef.current) {
            setIsDragging(true);
            setDragOffset({
                x: e.clientX - barPosition.x,
                y: e.clientY - barPosition.y
            });
        }
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && barRef.current) {
                const barRect = barRef.current.getBoundingClientRect();
                const newX = Math.max(0, Math.min(window.innerWidth - barRect.width, e.clientX - dragOffset.x));
                const newY = Math.max(0, Math.min(window.innerHeight - barRect.height, e.clientY - dragOffset.y));
                setBarPosition({ x: newX, y: newY });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleClearRecording = () => {
        setRecordedBlob(null);
        recordedChunks.current = [];
        displayStreamRef.current = null;
        setHasAudio(false);
    };

    const enablePictureInPicture = async () => {
        const video = previewVideoRef.current;
        if (!video || !document.pictureInPictureEnabled || document.pictureInPictureElement) {
            return false;
        }

        try {
            // Ensure video has a stream and is playing
            if (!video.srcObject || video.readyState < 2) {
                // Wait for video to be ready (with timeout)
                await Promise.race([
                    new Promise((resolve) => {
                        const checkReady = () => {
                            if (video.readyState >= 2) {
                                resolve();
                            } else {
                                setTimeout(checkReady, 100);
                            }
                        };
                        checkReady();
                    }),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000))
                ]);
            }

            // Try to play the video first (required for PiP)
            await video.play().catch(() => { });

            await video.requestPictureInPicture();
            return true;
        } catch (err) {
            console.error('[ScreenRecorder] PiP error:', err);
            return false;
        }
    };

    const exitPictureInPicture = async () => {
        if (document.pictureInPictureElement) {
            try {
                await document.exitPictureInPicture();
            } catch (err) {
                console.error('[ScreenRecorder] Exit PiP error:', err);
            }
        }
    };

    const handleStartRecording = async () => {
        recordedChunks.current = [];
        setHasAudio(false);

        try {
            // Check if getDisplayMedia is supported
            if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
                showDismissibleError('Your browser does not support screen recording. Please use Chrome, Firefox, or Edge.');
                return;
            }

            // Request screen capture with audio
            let displayStream;
            const displayMediaOptions = {
                video: {
                    cursor: "always",
                    displaySurface: "monitor"
                },
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100
                },
                // Prevent auto-switching to the captured tab
                selfBrowserSurface: "exclude",
                surfaceSwitching: "exclude",
                systemAudio: "include"
            };

            try {
                displayStream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);
                displayStreamRef.current = displayStream;

                // Set preview video source immediately and start playing
                if (previewVideoRef.current) {
                    previewVideoRef.current.srcObject = displayStream;
                    previewVideoRef.current.play().catch(() => { });
                }
            } catch (displayErr) {
                throw displayErr;
            }

            // Also capture microphone audio - explicitly request permission
            let micStream = null;
            const micOptions = {
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100
                }
            };

            try {
                // Check microphone permission status first
                let micPermissionStatus = null;
                try {
                    const permissionResult = await navigator.permissions.query({ name: 'microphone' });
                    micPermissionStatus = permissionResult.state;
                } catch (permErr) {
                    // Permissions API not supported, proceed with getUserMedia directly
                }

                if (micPermissionStatus === 'denied') {
                    showDismissibleError('Microphone permission denied. Please enable it in your browser settings to record audio.');
                } else {
                    // Request microphone access - this will prompt user if permission not yet granted
                    micStream = await navigator.mediaDevices.getUserMedia(micOptions);
                    if (micPermissionStatus === 'prompt') {
                        showDismissibleSuccess('Microphone access granted!');
                    }
                }
            } catch (audioErr) {
                // Handle specific permission errors
                if (audioErr.name === 'NotAllowedError' || audioErr.name === 'PermissionDeniedError') {
                    showDismissibleError('Microphone permission denied. Recording will continue without microphone audio.');
                } else if (audioErr.name === 'NotFoundError') {
                    showDismissibleToast('No microphone found. Recording will continue without microphone audio.', {
                        icon: '🎤'
                    });
                } else {
                    console.warn('[ScreenRecorder] Microphone access error:', audioErr);
                }
            }

            // Combine audio streams using AudioContext if we have multiple sources
            let combinedStream;
            const systemAudioTracks = displayStream.getAudioTracks();
            const micAudioTracks = micStream ? micStream.getAudioTracks() : [];
            const totalAudioTracks = systemAudioTracks.length + micAudioTracks.length;

            // Update audio status
            setHasAudio(totalAudioTracks > 0);

            if (systemAudioTracks.length > 0 && micAudioTracks.length > 0) {
                try {
                    const audioContext = new AudioContext();
                    const destination = audioContext.createMediaStreamDestination();

                    const systemSource = audioContext.createMediaStreamSource(new MediaStream(systemAudioTracks));
                    systemSource.connect(destination);

                    const micSource = audioContext.createMediaStreamSource(micStream);
                    micSource.connect(destination);

                    combinedStream = new MediaStream([
                        ...displayStream.getVideoTracks(),
                        ...destination.stream.getAudioTracks()
                    ]);

                    showDismissibleSuccess('Recording with system audio + microphone');
                } catch (mixErr) {
                    combinedStream = displayStream;
                    micAudioTracks.forEach(track => combinedStream.addTrack(track));
                }
            } else if (systemAudioTracks.length > 0) {
                combinedStream = displayStream;
                showDismissibleSuccess('Recording with system audio');
            } else if (micAudioTracks.length > 0) {
                combinedStream = new MediaStream([
                    ...displayStream.getVideoTracks(),
                    ...micAudioTracks
                ]);
                showDismissibleToast('Recording with microphone only (no system audio)', {
                    icon: '🎤'
                });
            } else {
                combinedStream = displayStream;
                showDismissibleToast('Recording without audio. To include audio:\n• Share a browser TAB (not window)\n• Check "Share audio" in the dialog', {
                    icon: '🔇'
                });
            }

            const mimeTypesToTry = [
                'video/webm;codecs=vp9,opus',
                'video/webm;codecs=vp8,opus',
                'video/webm;codecs=vp9',
                'video/webm;codecs=vp8',
                'video/webm'
            ];
            const mimeType = mimeTypesToTry.find(t => MediaRecorder.isTypeSupported(t)) || 'video/webm';

            mediaRecorder.current = new MediaRecorder(combinedStream, { mimeType });

            setIsRecording(true);

            mediaRecorder.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    recordedChunks.current.push(event.data);
                }
            };

            mediaRecorder.current.onerror = (event) => {
                console.error('[ScreenRecorder] MediaRecorder error:', event.error);
            };

            mediaRecorder.current.onstop = () => {
                exitPictureInPicture();

                displayStream.getTracks().forEach(track => track.stop());
                combinedStream.getTracks().forEach(track => track.stop());
                if (micStream) {
                    micStream.getTracks().forEach(track => track.stop());
                }
                displayStreamRef.current = null;

                const blob = new Blob(recordedChunks.current, { type: mimeType });
                setRecordedBlob(blob);
                onRecordingComplete(blob);
            };

            displayStream.getVideoTracks()[0].addEventListener('ended', () => {
                if (isRecording) {
                    handleStopRecording();
                }
            });

            mediaRecorder.current.start();
            showDismissibleSuccess('Screen recording started');

            // Auto-enable Picture-in-Picture after video is ready
            setTimeout(async () => {
                if (document.pictureInPictureEnabled && previewVideoRef.current?.srcObject) {
                    const success = await enablePictureInPicture();
                    if (success) {
                        showDismissibleToast('Preview window opened! It will stay visible even if you switch tabs.', {
                            icon: '🖼️'
                        });
                    }
                }
            }, 1000);

        } catch (err) {
            console.error('[ScreenRecorder] Screen recording error:', err);
            setIsRecording(false);
            displayStreamRef.current = null;
            setHasAudio(false);

            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                showDismissibleError('Screen recording permission denied or cancelled.');
            } else if (err.name === 'NotFoundError') {
                showDismissibleError('No screen available to record.');
            } else if (err.name === 'NotReadableError') {
                showDismissibleError('Screen is already being captured by another application.');
            } else if (err.name === 'SecurityError') {
                showDismissibleError('Screen recording requires HTTPS. Please check your connection.');
            } else {
                showDismissibleError('Unable to start screen recording: ' + err.message);
            }
        }
    };

    const handleStopRecording = () => {
        setIsRecording(false);
        setHasAudio(false);
        if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
            mediaRecorder.current.stop();
            showDismissibleSuccess('Screen recording stopped');
        }
    };

    return (
        <>
            {/* Video element for PiP - positioned off-screen when not in PiP */}
            <video
                ref={previewVideoRef}
                autoPlay
                muted
                playsInline
                style={{
                    position: 'fixed',
                    top: '-9999px',
                    left: '-9999px',
                    width: '1px',
                    height: '1px',
                    opacity: 0,
                    pointerEvents: 'none'
                }}
            />

            {!recordedBlob && !isRecording && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleStartRecording}
                        >
                            <MonitorUp className="h-5 w-5" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>Record Screen</TooltipContent>
                </Tooltip>
            )}

            {/* Recording Status Bar - shown when recording, draggable, starts at top center */}
            {isRecording && (
                <div
                    ref={barRef}
                    className={cn(
                        "fixed z-[9999] flex items-center gap-2 px-3 py-2 bg-popover/95 backdrop-blur-sm rounded-full shadow-lg border",
                        isDragging ? "cursor-grabbing" : "cursor-grab"
                    )}
                    style={{
                        left: barPosition.x,
                        top: barPosition.y,
                        userSelect: 'none'
                    }}
                    onMouseDown={handleMouseDown}
                >
                    {/* Recording indicator */}
                    <div className="flex items-center gap-2 px-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-destructive animate-pulse" />
                        <span className="font-mono text-sm font-medium text-foreground">
                            {formatTime(recordingTime)}
                        </span>
                    </div>

                    {/* Divider */}
                    <div className="w-px h-5 bg-border" />

                    {/* Audio status badge */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Badge
                                variant={hasAudio ? "default" : "secondary"}
                                className={cn(
                                    "gap-1 cursor-default",
                                    hasAudio
                                        ? "bg-green-500/15 text-green-600 dark:text-green-400 hover:bg-green-500/20 border-green-500/30"
                                        : "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-500/20 border-yellow-500/30"
                                )}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                {hasAudio ? (
                                    <>
                                        <Volume2 className="h-3 w-3" />
                                        <span>Audio</span>
                                    </>
                                ) : (
                                    <>
                                        <VolumeX className="h-3 w-3" />
                                        <span>No Audio</span>
                                    </>
                                )}
                            </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                            {hasAudio
                                ? "Audio is being recorded"
                                : "No audio - share a browser TAB and check 'Share audio'"}
                        </TooltipContent>
                    </Tooltip>

                    {/* Divider */}
                    <div className="w-px h-5 bg-border" />

                    {/* PiP toggle button */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant={isPipActive ? "secondary" : "ghost"}
                                size="icon"
                                className={cn(
                                    "h-8 w-8 rounded-full",
                                    isPipActive && "bg-primary/10 text-primary hover:bg-primary/20"
                                )}
                                onClick={() => {
                                    isPipActive ? exitPictureInPicture() : enablePictureInPicture();
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <PictureInPicture2 className="h-4 w-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            {isPipActive ? "Close Preview" : "Show Preview (Picture-in-Picture)"}
                        </TooltipContent>
                    </Tooltip>

                    {/* Stop button */}
                    <Button
                        variant="destructive"
                        size="sm"
                        className="h-8 px-3 rounded-full gap-1.5"
                        onClick={handleStopRecording}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <Square className="h-3 w-3 fill-current" />
                    </Button>
                </div>
            )}
        </>
    );
};

export default ScreenRecorder;
